import type {
  ApprovalDescriptor,
  ChoiceOption,
  IconName,
  OnboardingTools,
  WatchOutcome,
} from "./onboarding-chat";

export type Beat =
  | { kind: "pause"; ms: number }
  | { kind: "say"; text: string; warm?: boolean }
  | {
      kind: "tool";
      id: string;
      name: keyof OnboardingTools & string;
      input: unknown;
      output: unknown;
      takes?: number;
      echoSelection?: boolean;
    }
  | {
      kind: "tool-approval";
      id: string;
      name: keyof OnboardingTools & string;
      input: unknown;
      reason: string;
      descriptor?: ApprovalDescriptor;
    }
  | { kind: "tool-output"; id: string; output: unknown; takes?: number }
  | {
      kind: "tool-failure";
      id: string;
      name: keyof OnboardingTools & string;
      input: unknown;
      errorText: string;
      takes?: number;
    }
  | { kind: "watch"; id: string; data: unknown }
  | { kind: "data"; part: string; id?: string; data: unknown }
  | { kind: "drop" }
  | { kind: "error"; text: string };

export type MockScenario = {
  id: string;
  label: string;
  description: string;
  opening: Beat[];
  turns: Beat[][];
  declineTurn?: Beat[];
  watch: { attempts: number; outcome: WatchOutcome; detail?: string };
};

const PROGRAM_NAME = "Riverside Urgent Care";
// 15 characters, not 16 — `createEpocusProgram` cuts to 15 because Deep Echo
// silently truncated a 16-char title on acceptance.
const AE_TITLE = "RIVERSIDE_URGE";
const STUDY_UID = "1.2.826.0.1.3680043.8.498.10714829313295152";
const TOTAL_STEPS = 5;

const progress = (step: number): Beat => ({
  kind: "data",
  part: "progress",
  data: { step, total: TOTAL_STEPS },
});

const choices = (
  options: ChoiceOption[],
  extra?: {
    mode?: "single" | "multi";
    selected?: string[];
    submitLabel?: string;
  },
): Beat => ({
  kind: "data",
  part: "choices",
  data: { mode: extra?.mode ?? "single", options, ...extra },
});

const milestone = (title: string, detail: string): Beat => ({
  kind: "data",
  part: "milestone",
  data: { title, detail },
});

const SCAN_TYPES: ChoiceOption[] = [
  { id: "fast", label: "FAST / trauma" },
  { id: "cardiac", label: "Cardiac" },
  { id: "lung", label: "Lung" },
  { id: "ob", label: "OB" },
];

const DEVICES: ChoiceOption[] = [
  { id: "butterfly", label: "Butterfly iQ3", icon: "probe" },
  { id: "vscan", label: "GE Vscan Air", icon: "probe" },
  { id: "other", label: "Something else", style: "ghost" },
];

const PAYOFF_STEPS: { label: string; icon: IconName; amount?: string }[] = [
  { label: "Scan captured on your device", icon: "probe" },
  { label: "Worksheet auto-filled, ready to sign", icon: "doc" },
  { label: "Coded for a guided procedure", icon: "scan", amount: "+ $148" },
];

const buildProgram: Beat[] = [
  progress(2),
  { kind: "pause", ms: 400 },
  {
    kind: "tool",
    id: "t-exams",
    name: "setEpocusProgramEnabledExamTypes",
    input: { examTypes: ["FAST"] },
    output: { examTypes: ["FAST"] },
    echoSelection: true,
    takes: 900,
  },
  {
    kind: "say",
    warm: true,
    text: "Done — your program's worksheets are set. *Now the fun part:* let's get your device talking to us.",
  },
  milestone("Program built", "1 of 3 done · that was the slow part"),
  { kind: "pause", ms: 600 },
  { kind: "say", text: "What are you setting up with?" },
  choices(DEVICES),
];

const deviceAndSend: Beat[] = [
  progress(3),
  { kind: "pause", ms: 400 },
  {
    kind: "say",
    text: "Nice. I'll generate the code to set on it, then send a test scan to prove it lands — no guessing.",
  },
  {
    kind: "tool-approval",
    id: "t-create",
    name: "createEpocusProgram",
    input: { name: PROGRAM_NAME },
    reason:
      "The code your device sends to is derived from this name and cannot be changed later.",
    descriptor: {
      prompt: `Create the program as "${PROGRAM_NAME}"?`,
      confirmLabel: "Yes, create it",
      declineLabel: "Change the name",
    },
  },
];

const handover: Beat[] = [
  {
    kind: "tool-output",
    id: "t-create",
    takes: 1200,
    output: {
      programId: "prog_01J9",
      name: PROGRAM_NAME,
      dimseAeTitle: AE_TITLE,
      ingressRouteCount: 2,
    },
  },
  { kind: "pause", ms: 300 },
  {
    kind: "tool",
    id: "t-send",
    name: "sendTestStudy",
    input: { programId: "prog_01J9" },
    output: { studyInstanceUid: STUDY_UID },
    takes: 1100,
  },
  progress(4),
  {
    kind: "watch",
    id: "w-1",
    data: {
      programId: "prog_01J9",
      since: "2026-09-30T09:00:00.000Z",
      expectedProgramName: PROGRAM_NAME,
      instructions: `Set the destination AE title to ${AE_TITLE} on the device, then send any study.`,
    },
  },
];

const livePayoff: Beat[] = [
  { kind: "pause", ms: 300 },
  milestone("Scan landed — you're live", "Riverside is receiving scans now"),
  { kind: "pause", ms: 600 },
  {
    kind: "say",
    warm: true,
    text: "Here's what being live actually means for you — watch one scan become billable.",
  },
  {
    kind: "data",
    part: "payoff",
    data: {
      title: "From scan to billable record",
      steps: PAYOFF_STEPS,
      footer:
        "That's revenue you were leaving on the table. Every scan, from now on.",
    },
  },
  { kind: "pause", ms: 3200 },
  {
    kind: "data",
    part: "cta",
    data: { label: "Go to ePOCUS home", destination: "/epocus/home" },
  },
  {
    kind: "say",
    text: "Your team can do this for every scan now. Want me to walk them through their first one?",
  },
  choices([
    {
      id: "invite",
      label: "Yes, invite my team",
      icon: "people",
      style: "primary",
    },
    { id: "later", label: "I'll do it later", style: "ghost" },
  ]),
];

const KNOWN: MockScenario = {
  id: "known",
  label: "Known from CRM",
  description: "The agent opens with what it already knows and confirms it.",
  opening: [
    progress(0),
    { kind: "pause", ms: 500 },
    {
      kind: "say",
      warm: true,
      text: "Hi Dr Ferre — I'm Dragonfly. I'll get *Riverside Urgent Care* set up and prove it's working before we're done. Takes about two minutes.",
    },
    { kind: "pause", ms: 500 },
    {
      kind: "say",
      text: "I pulled this from your notes with the team — does it look right?",
    },
    {
      kind: "data",
      part: "summary",
      data: {
        title: "From your account",
        rows: [
          { label: "Site", value: PROGRAM_NAME, pill: "confirmed" },
          { label: "You", value: "Dr Rob Ferre · Program admin" },
          {
            label: "Does",
            value: "FAST / point-of-care",
            pill: "from call notes",
          },
        ],
      },
    },
    choices([
      { id: "yes", label: "Yes, that's right", style: "primary" },
      { id: "tweak", label: "Mostly — let me tweak it", style: "ghost" },
    ]),
  ],
  declineTurn: [
    { kind: "pause", ms: 400 },
    {
      kind: "say",
      text: "Nothing created — the name is the one thing we cannot take back, so it is worth getting right. What should I call it?",
    },
    {
      kind: "data",
      part: "text-input",
      data: { placeholder: "e.g. Riverside Urgent Care" },
    },
  ],
  turns: [
    [
      progress(1),
      { kind: "pause", ms: 400 },
      {
        kind: "say",
        text: "Great — that saves us the setup questions. Just one to confirm: which scans does Riverside do? I've pre-ticked FAST from your notes.",
      },
      choices(SCAN_TYPES, {
        mode: "multi",
        selected: ["fast"],
        submitLabel: "Looks right — build it",
      }),
    ],
    buildProgram,
    deviceAndSend,
    handover,
    livePayoff,
  ],
  watch: { attempts: 4, outcome: "ROUTED" },
};

const COLD: MockScenario = {
  ...KNOWN,
  id: "cold",
  label: "No CRM record",
  description: "Nothing known about the site yet, so the agent asks.",
  opening: [
    progress(0),
    { kind: "pause", ms: 500 },
    {
      kind: "say",
      warm: true,
      text: "Hi — I'm Dragonfly. I'll get your program set up and prove it's working before we're done.",
    },
    { kind: "pause", ms: 400 },
    {
      kind: "say",
      text: "I don't have your site in our records yet — no problem, it's a couple of taps. What kind of site is this?",
    },
    choices([
      { id: "solo", label: "Solo / small practice", icon: "people" },
      { id: "urgent", label: "Urgent care", icon: "people" },
      { id: "hospital", label: "Hospital department", icon: "people" },
      { id: "other", label: "Something else", style: "ghost" },
    ]),
  ],
  turns: [
    [
      progress(1),
      { kind: "pause", ms: 400 },
      {
        kind: "say",
        text: "Got it. And what do you scan? Tap any that apply.",
      },
      choices(SCAN_TYPES, { mode: "multi", submitLabel: "Continue" }),
    ],
    buildProgram,
    deviceAndSend,
    handover,
    livePayoff,
  ],
};

const FREE_TEXT: MockScenario = {
  ...KNOWN,
  id: "free-text",
  label: "Agent asks for text",
  description:
    "The one case the composer appears: something taps cannot answer.",
  opening: [
    progress(0),
    { kind: "pause", ms: 500 },
    {
      kind: "say",
      warm: true,
      text: "Hi — I'm Dragonfly. What should I call this program? It becomes the code your device sends to, so it is worth getting right.",
    },
    {
      kind: "data",
      part: "text-input",
      data: { placeholder: "e.g. Riverside Urgent Care" },
    },
  ],
  turns: [
    [
      progress(1),
      { kind: "pause", ms: 400 },
      { kind: "say", text: "Got it. Which scans does the site do?" },
      choices(SCAN_TYPES, { mode: "multi", submitLabel: "Continue" }),
    ],
    buildProgram,
    deviceAndSend,
    handover,
    livePayoff,
  ],
};

const UNROUTED: MockScenario = {
  ...KNOWN,
  id: "unrouted",
  label: "Caught failure",
  description:
    "The study arrives under the wrong code, and the agent says why.",
  turns: [
    KNOWN.turns[0],
    buildProgram,
    deviceAndSend,
    handover,
    [
      { kind: "pause", ms: 400 },
      {
        kind: "say",
        text: "A study arrived, so the network path is fine — but it came in addressed to RIVERSIDE, and this program's code is RIVERSIDE_URGE. It landed unattributed, which is why nobody would have seen it and nothing would have errored.\n\nCorrect the code on the device and I'll send another test scan. I won't call the site live until one lands in the right program.",
      },
    ],
  ],
  watch: {
    attempts: 4,
    outcome: "UNROUTED",
    detail:
      "Study presented called AE 'RIVERSIDE' — no ingress route matches it.",
  },
};

const DROPPED: MockScenario = {
  ...KNOWN,
  id: "dropped",
  label: "Connection drops",
  description: "The stream dies mid-turn and the rest arrives on retry.",
  turns: [
    [
      progress(1),
      { kind: "pause", ms: 400 },
      { kind: "say", text: "Great — that saves us the setup questions." },
      { kind: "drop" },
      {
        kind: "say",
        text: "Which scans does Riverside do? I've pre-ticked FAST from your notes.",
      },
      choices(SCAN_TYPES, {
        mode: "multi",
        selected: ["fast"],
        submitLabel: "Looks right — build it",
      }),
    ],
    buildProgram,
    deviceAndSend,
    handover,
    livePayoff,
  ],
};

export const MOCK_SCENARIOS: MockScenario[] = [
  KNOWN,
  COLD,
  FREE_TEXT,
  UNROUTED,
  DROPPED,
];

export const DEFAULT_SCENARIO_ID = KNOWN.id;

export const findScenario = (id: string | null | undefined): MockScenario =>
  MOCK_SCENARIOS.find((scenario) => scenario.id === id) ?? KNOWN;

const MOCK_VIEWER_NAME = "Dr Rob Ferre";

const replaceStringLeaves = (
  value: unknown,
  from: string,
  to: string,
): unknown => {
  if (typeof value === "string") return value.replaceAll(from, to);
  if (Array.isArray(value)) {
    return value.map((item) => replaceStringLeaves(item, from, to));
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        replaceStringLeaves(item, from, to),
      ]),
    );
  }
  return value;
};

export const withViewerName = (
  scenario: MockScenario,
  viewerName?: string,
): MockScenario => {
  if (!viewerName) return scenario;
  return replaceStringLeaves(
    scenario,
    MOCK_VIEWER_NAME,
    viewerName,
  ) as MockScenario;
};
